import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import {
  getAnalytics,
  getVisitorStats,
  getFunnelAnalyses,
  getFunnelPaid,
  getFunnelAbandoned,
  getFunnelIkomatchClicks,
  getFunnelInvestors,
  getFunnelReferrals,
  getBusinessOverview,
  type AnalyticsDay,
  type VisitorStats,
  type FunnelAnalysisRow,
  type FunnelPaidRow,
  type FunnelAbandonedRow,
  type FunnelIkomatchRow,
  type FunnelInvestorRow,
  type FunnelReferralRow,
} from "@/lib/admin.functions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useRoleGuard } from "@/hooks/use-role-guard";
import type { BusinessOverview } from "@/lib/insights";
import { SidebarLayout } from "@/components/sidebar-layout";
import { adminSidebarItemsForRole } from "@/components/admin-shell";
import { staffSidebarItems } from "@/components/staff-shell";

export const Route = createFileRoute("/analytics")({
  head: () => ({
    meta: [
      { title: "Analytics - DeckScore" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AnalyticsPage,
});

function AnalyticsPage() {
  const { status, role } = useRoleGuard(["admin", "super_admin"]);
  if (status !== "ok") {
    return (
      <div className="min-h-screen bg-bg grid place-items-center text-ink-secondary">
        Loading…
      </div>
    );
  }
  const items =
    role === "super_admin" || role === "admin" ? adminSidebarItemsForRole(role) : staffSidebarItems;
  return (
    <SidebarLayout area="Analytics" items={items}>
      <AnalyticsDashboard role={role} />
    </SidebarLayout>
  );
}


type Data = { daily: AnalyticsDay[]; sources: { source: string; leads: number }[]; currencies: string[]; generatedAt: string };
type Preset = "today" | "7d" | "14d" | "30d" | "all";

function money(minor: number, currency: string) {
  const c = currency.toUpperCase();
  const symbol = c === "USD" ? "$" : c === "INR" ? "₹" : `${c} `;
  return `${symbol}${(minor / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function pct(num: number, den: number) {
  if (!den) return "-";
  return `${((num / den) * 100).toFixed(1)}%`;
}

function AnalyticsDashboard({ role }: { role: string | null | undefined }) {
  const run = useServerFn(getAnalytics);
  const runVisitors = useServerFn(getVisitorStats);
  const [data, setData] = useState<Data | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [preset, setPreset] = useState<Preset>("14d");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [customized, setCustomized] = useState(false);
  const [visitors, setVisitors] = useState<VisitorStats | null>(null);

  useEffect(() => {
    run()
      .then((d) => setData(d as Data))
      .catch((e) => setErr(e?.message ?? "Failed to load analytics"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      runVisitors()
        .then((v) => { if (!cancelled) setVisitors(v); })
        .catch(() => { /* leave stale */ });
    };
    tick();
    const id = setInterval(tick, 20_000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const latestDay = data?.daily.length ? data.daily[data.daily.length - 1].d : "";
  const firstDay = data?.daily.length ? data.daily[0].d : "";

  useEffect(() => {
    if (!data || customized) return;
    if (!latestDay) return;
    const end = new Date(latestDay + "T00:00:00Z");
    let startDate = new Date(end);
    if (preset === "today") startDate = end;
    else if (preset === "7d") startDate.setUTCDate(end.getUTCDate() - 6);
    else if (preset === "14d") startDate.setUTCDate(end.getUTCDate() - 13);
    else if (preset === "30d") startDate.setUTCDate(end.getUTCDate() - 29);
    else if (preset === "all") startDate = new Date(firstDay + "T00:00:00Z");
    setFrom(startDate.toISOString().slice(0, 10));
    setTo(end.toISOString().slice(0, 10));
  }, [preset, data, latestDay, firstDay, customized]);

  const inRange = useMemo(() => {
    if (!data || !from || !to) return [] as AnalyticsDay[];
    return data.daily.filter((r) => r.d >= from && r.d <= to);
  }, [data, from, to]);

  const prevRange = useMemo(() => {
    if (!data || !from || !to) return [] as AnalyticsDay[];
    const start = new Date(from + "T00:00:00Z");
    const end = new Date(to + "T00:00:00Z");
    const lenDays = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
    const pEnd = new Date(start);
    pEnd.setUTCDate(pEnd.getUTCDate() - 1);
    const pStart = new Date(pEnd);
    pStart.setUTCDate(pEnd.getUTCDate() - (lenDays - 1));
    const pFrom = pStart.toISOString().slice(0, 10);
    const pTo = pEnd.toISOString().slice(0, 10);
    return data.daily.filter((r) => r.d >= pFrom && r.d <= pTo);
  }, [data, from, to]);

  const sum = (rows: AnalyticsDay[]) =>
    rows.reduce(
      (acc, r) => {
        acc.leads += r.leads;
        acc.decks += r.decks;
        acc.scored += r.scored;
        acc.paid += r.paid;
        for (const [c, v] of Object.entries(r.revenue_by_currency ?? {})) {
          acc.revenue_by_currency[c] = (acc.revenue_by_currency[c] ?? 0) + v;
        }
        for (const [c, v] of Object.entries(r.paid_by_currency ?? {})) {
          acc.paid_by_currency[c] = (acc.paid_by_currency[c] ?? 0) + v;
        }
        acc.fulfilled += r.fulfilled;
        return acc;
      },
      { leads: 0, decks: 0, scored: 0, paid: 0, revenue_by_currency: {} as Record<string, number>, paid_by_currency: {} as Record<string, number>, fulfilled: 0 },
    );

  const cur = sum(inRange);
  const prev = sum(prevRange);

  const delta = (a: number, b: number) => {
    if (!b) return a > 0 ? { pct: 100, up: true } : null;
    const d = ((a - b) / b) * 100;
    return { pct: Math.abs(d), up: d >= 0 };
  };

  if (err) {
    return <div className="text-[14px] text-red">{err}</div>;
  }
  if (!data) {
    return <div className="text-ink-secondary">Loading analytics…</div>;
  }

  const revenueCurrencies = Object.keys(cur.revenue_by_currency).sort();
  const revenueDisplay = revenueCurrencies.length
    ? revenueCurrencies.map((c) => money(cur.revenue_by_currency[c] ?? 0, c)).join(" · ")
    : money(0, "usd");
  const avgOrderDisplay = revenueCurrencies.length
    ? revenueCurrencies
        .map((c) => {
          const paid = cur.paid_by_currency[c] ?? 0;
          const rev = cur.revenue_by_currency[c] ?? 0;
          return paid ? money(Math.round(rev / paid), c) : `${c.toUpperCase()} -`;
        })
        .join(" · ")
    : "-";
  const primaryCur = revenueCurrencies[0] ?? "usd";
  const primaryRev = cur.revenue_by_currency[primaryCur] ?? 0;
  const primaryPrevRev = prev.revenue_by_currency[primaryCur] ?? 0;

  return (
    <>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="font-script text-accent text-[22px] leading-none">metrics</p>
          <h1 className="font-display text-[28px] font-semibold mt-2">Analytics</h1>
        </div>
        <div className="text-[12px] text-ink-tertiary">
          as of {new Date(data.generatedAt).toLocaleString()}
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-surface border border-line rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {(["today", "7d", "14d", "30d", "all"] as Preset[]).map((p) => (
            <button
              key={p}
              onClick={() => {
                setCustomized(false);
                setPreset(p);
              }}
              className={`px-3 py-1.5 rounded-full text-[12px] font-medium border ${
                !customized && preset === p
                  ? "bg-ink text-on-black border-ink"
                  : "bg-surface text-ink-secondary border-line hover:text-ink"
              }`}
            >
              {p === "today" ? "Today" : p === "all" ? "All" : p}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-[13px] text-ink-secondary">
          <label>From</label>
          <input
            type="date"
            value={from}
            min={firstDay}
            max={latestDay}
            onChange={(e) => {
              setCustomized(true);
              setFrom(e.target.value);
            }}
            className="bg-surface border border-line rounded-md px-2 py-1 text-ink"
          />
          <label>To</label>
          <input
            type="date"
            value={to}
            min={firstDay}
            max={latestDay}
            onChange={(e) => {
              setCustomized(true);
              setTo(e.target.value);
            }}
            className="bg-surface border border-line rounded-md px-2 py-1 text-ink"
          />
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi
          label="Free → Paid"
          value={pct(cur.paid, cur.scored)}
          highlight
          hint="paid ÷ scored"
        />
        <Kpi label="Leads" value={cur.leads.toLocaleString()} delta={delta(cur.leads, prev.leads)} />
        <Kpi label="Activation" value={pct(cur.scored, cur.decks)} hint="scored ÷ decks" />
        <Kpi label="Paid" value={cur.paid.toLocaleString()} delta={delta(cur.paid, prev.paid)} />
        <Kpi
          label="Revenue"
          value={revenueDisplay}
          delta={delta(primaryRev, primaryPrevRev)}
          hint={revenueCurrencies.length > 1 ? `trend: ${primaryCur.toUpperCase()}` : undefined}
        />
        <Kpi
          label="Avg order"
          value={avgOrderDisplay}
        />

        <Kpi label="Fulfillment" value={pct(cur.fulfilled, cur.paid)} hint="fulfilled ÷ paid" />
        <Kpi label="Decks uploaded" value={cur.decks.toLocaleString()} />
        <Kpi
          label="Total visitors"
          value={visitors && visitors.configured ? visitors.totalVisitors.toLocaleString() : "-"}
          hint={visitors && !visitors.configured ? "Add POSTHOG_PERSONAL_API_KEY to enable" : "all-time (PostHog)"}
        />
        <Kpi
          label="Pageviews"
          value={visitors && visitors.configured ? visitors.totalPageviews.toLocaleString() : "-"}
          hint={visitors && visitors.configured ? "all-time (PostHog)" : undefined}
        />
        <LiveKpi
          count={visitors && visitors.configured ? visitors.liveVisitors : null}
          configured={!!visitors && visitors.configured}
        />
      </div>

      {role === "super_admin" && <BusinessHealth from={from} to={to} />}

      <LeadMagnetFunnel from={from} to={to} />




      {/* Funnel */}
      <div className="bg-surface border border-line rounded-2xl p-5 mb-6">
        <h2 className="font-display text-[18px] font-semibold mb-4">Acquisition funnel</h2>
        <Funnel
          steps={[
            { label: "Leads", value: cur.leads, conv: null },
            { label: "Decks uploaded", value: cur.decks, conv: pct(cur.decks, cur.leads) },
            { label: "Scored", value: cur.scored, conv: pct(cur.scored, cur.decks) },
            { label: "Paid", value: cur.paid, conv: pct(cur.paid, cur.scored) },
            { label: "Fulfilled", value: cur.fulfilled, conv: pct(cur.fulfilled, cur.paid) },
          ]}
        />
      </div>

      {/* Trend */}
      <div className="bg-surface border border-line rounded-2xl p-5 mb-6">
        <h2 className="font-display text-[18px] font-semibold mb-4">Daily trend</h2>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <LineChart data={inRange} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="var(--color-line)" strokeDasharray="3 3" />
              <XAxis dataKey="d" tick={{ fontSize: 11, fill: "var(--color-ink-tertiary)" }} />
              <YAxis tick={{ fontSize: 11, fill: "var(--color-ink-tertiary)" }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  background: "var(--color-surface)",
                  border: "1px solid var(--color-line)",
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="leads" stroke="#B79AEF" strokeWidth={2} dot={false} name="Leads" />
              <Line type="monotone" dataKey="scored" stroke="#4A9EDB" strokeWidth={2} dot={false} name="Scored" />
              <Line type="monotone" dataKey="paid" stroke="#5FBF8F" strokeWidth={2} dot={false} name="Paid" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Leads by source */}
      <div className="bg-surface border border-line rounded-2xl p-5 mb-6">
        <h2 className="font-display text-[18px] font-semibold mb-4">Leads by source (last 90d)</h2>
        {data.sources.length === 0 ? (
          <div className="text-ink-tertiary text-[13px]">No leads yet.</div>
        ) : (
          <ul className="space-y-2">
            {data.sources.map((s) => {
              const total = data.sources.reduce((a, b) => a + b.leads, 0);
              const share = total ? (s.leads / total) * 100 : 0;
              return (
                <li key={s.source} className="flex items-center gap-3 text-[13px]">
                  <div className="w-40 truncate text-ink-secondary">{s.source}</div>
                  <div className="flex-1 h-2 bg-surface-sunken rounded-full overflow-hidden">
                    <div className="h-full bg-accent" style={{ width: `${share}%` }} />
                  </div>
                  <div className="w-24 text-right tabular-nums">
                    {s.leads} · {share.toFixed(1)}%
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="text-[12px] text-ink-tertiary">
        Free→Paid over a date range is an approximation (counted by each event's own day, not
        strict cohort).
      </p>
    </>
  );
}

function Kpi({
  label,
  value,
  hint,
  delta,
  highlight,
}: {
  label: string;
  value: string;
  hint?: string;
  delta?: { pct: number; up: boolean } | null;
  highlight?: boolean;
}) {
  return (
    <div
      className={`border border-line rounded-2xl p-5 ${
        highlight ? "bg-accent-soft" : "bg-surface"
      }`}
    >
      <div className="text-[12px] uppercase tracking-wider text-ink-tertiary">{label}</div>
      <div
        className={`mt-2 font-display font-semibold ${
          highlight ? "text-[32px]" : "text-[24px]"
        }`}
      >
        {value}
      </div>
      <div className="mt-1 flex items-center gap-2 text-[12px] text-ink-tertiary">
        {hint && <span>{hint}</span>}
        {delta && (
          <span
            className={delta.up ? "text-[#2E7D57] font-medium" : "text-red font-medium"}
          >
            {delta.up ? "▲" : "▼"} {delta.pct.toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  );
}

function Funnel({
  steps,
}: {
  steps: { label: string; value: number; conv: string | null }[];
}) {
  const max = Math.max(...steps.map((s) => s.value), 1);
  return (
    <ul className="space-y-3">
      {steps.map((s) => {
        const width = (s.value / max) * 100;
        return (
          <li key={s.label} className="text-[13px]">
            <div className="flex justify-between mb-1">
              <span className="text-ink-secondary">{s.label}</span>
              <span className="tabular-nums text-ink">
                {s.value.toLocaleString()}
                {s.conv && <span className="text-ink-tertiary ml-2">({s.conv})</span>}
              </span>
            </div>
            <div className="h-3 bg-surface-sunken rounded-full overflow-hidden">
              <div className="h-full bg-accent" style={{ width: `${width}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function LiveKpi({ count, configured }: { count: number | null; configured: boolean }) {
  const isLive = configured && count !== null && count > 0;
  return (
    <div className="border border-line rounded-2xl p-5 bg-surface">
      <div className="text-[12px] uppercase tracking-wider text-ink-tertiary flex items-center gap-1.5">
        <span
          className={`inline-block h-2 w-2 rounded-full ${isLive ? "bg-[#2E7D57] animate-pulse" : "bg-ink-tertiary/40"}`}
          aria-hidden
        />
        Live now
      </div>
      <div
        className={`mt-2 font-display font-semibold text-[24px] ${isLive ? "text-[#2E7D57]" : ""}`}
      >
        {configured && count !== null ? count.toLocaleString() : "-"}
      </div>
      <div className="mt-1 text-[12px] text-ink-tertiary">
        {configured ? "last 5 minutes · updates every 20s" : "Add POSTHOG_PERSONAL_API_KEY to enable"}
      </div>
    </div>
  );
}

// -------------------- Lead-magnet funnel --------------------

type FunnelData = {
  analyses: { count: number; rows: FunnelAnalysisRow[] } | null;
  paid: { count: number; rows: FunnelPaidRow[] } | null;
  abandoned: { count: number; rows: FunnelAbandonedRow[] } | null;
  ikomatch:
    | { count: number; rows: FunnelIkomatchRow[]; breakdown: { placement: string; count: number }[] }
    | null;
  investors: { count: number; rows: FunnelInvestorRow[] } | null;
  referrals: { count: number; rows: FunnelReferralRow[] } | null;
};

type ModalKey = "analyses" | "paid" | "abandoned" | "ikomatch" | "investors" | "referrals" | null;

function LeadMagnetFunnel({ from, to }: { from: string; to: string }) {
  const runA = useServerFn(getFunnelAnalyses);
  const runP = useServerFn(getFunnelPaid);
  const runAb = useServerFn(getFunnelAbandoned);
  const runI = useServerFn(getFunnelIkomatchClicks);
  const runInv = useServerFn(getFunnelInvestors);
  const runRef = useServerFn(getFunnelReferrals);
  const [data, setData] = useState<FunnelData>({
    analyses: null, paid: null, abandoned: null, ikomatch: null, investors: null, referrals: null,
  });
  const [open, setOpen] = useState<ModalKey>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    const payload = { data: { from, to } };
    Promise.all([runA(payload), runP(payload), runAb(payload), runI(payload), runInv(payload), runRef(payload)])
      .then(([a, p, ab, i, inv, r]) => {
        if (cancelled) return;
        setData({ analyses: a, paid: p, abandoned: ab, ikomatch: i, investors: inv, referrals: r });
      })
      .catch((e) => !cancelled && setErr(e?.message ?? "Failed to load funnel"));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  const analysesCount = data.analyses?.count ?? 0;
  const paidCount = data.paid?.count ?? 0;
  const abandonedCount = data.abandoned?.count ?? 0;
  const ikoCount = data.ikomatch?.count ?? 0;
  const investorsCount = data.investors?.count ?? 0;
  const referralsCount = data.referrals?.count ?? 0;

  const conv = (num: number, den: number) => (den ? `${((num / den) * 100).toFixed(1)}%` : "-");

  // Targets per 200 visitors
  const targetAnalyses = 50, targetPaid = 5, targetIko = 30;

  return (
    <div className="bg-surface border border-line rounded-2xl p-5 mb-6">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-display text-[18px] font-semibold">Lead-magnet funnel</h2>
          <p className="text-[12px] text-ink-tertiary mt-1">
            Click any card to see the underlying records. Visitors tracked in PostHog - targets shown per 200 visitors.
          </p>
        </div>
      </div>
      {err && <div className="text-[13px] text-red mb-3">{err}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
        <FunnelCard
          label="Free analyses"
          count={analysesCount}
          hint={`Target: ${targetAnalyses}/200 visitors`}
          onClick={() => setOpen("analyses")}
        />
        <FunnelCard
          label="Paid submissions"
          count={paidCount}
          hint={`Conv (paid/analyses): ${conv(paidCount, analysesCount)} · Target: ${targetPaid}/200`}
          onClick={() => setOpen("paid")}
        />
        <FunnelCard
          label="Abandoned checkouts"
          count={abandonedCount}
          hint={`Started but not paid · drop-off: ${conv(abandonedCount, abandonedCount + paidCount)}`}
          onClick={() => setOpen("abandoned")}
        />
        <FunnelCard
          label="ikomatch clicks"
          count={ikoCount}
          hint={`Conv (clicks/analyses): ${conv(ikoCount, analysesCount)} · Target: ${targetIko}/200`}
          onClick={() => setOpen("ikomatch")}
        />
        <FunnelCard
          label="Investors registered"
          count={investorsCount}
          onClick={() => setOpen("investors")}
        />
        <FunnelCard
          label="Referrals delivered"
          count={referralsCount}
          onClick={() => setOpen("referrals")}
        />
      </div>

      {data.ikomatch && data.ikomatch.breakdown.length > 0 && (
        <div className="mt-4 text-[12px] text-ink-secondary">
          <span className="text-ink-tertiary">By placement: </span>
          {data.ikomatch.breakdown.map((b, i) => (
            <span key={b.placement}>
              {i > 0 && " · "}
              <span className="text-ink">{b.placement}</span> {b.count}
            </span>
          ))}
        </div>
      )}

      <FunnelModal
        open={open === "analyses"}
        onClose={() => setOpen(null)}
        title="Free analyses"
        count={analysesCount}
        columns={["Email", "Company / file", "Score", "Created"]}
        rows={(data.analyses?.rows ?? []).map((r) => [
          r.email ?? "Anonymous",
          r.company,
          r.overall_score ?? "-",
          fmtDate(r.created_at),
        ])}
      />
      <FunnelModal
        open={open === "paid"}
        onClose={() => setOpen(null)}
        title="Paid submissions"
        count={paidCount}
        columns={["Email", "Amount", "Paid at", "Fulfillment"]}
        rows={(data.paid?.rows ?? []).map((r) => [
          r.email ?? "-",
          r.amount_cents != null
            ? `${(r.currency ?? "usd").toUpperCase()} ${(r.amount_cents / 100).toFixed(2)}`
            : "-",
          fmtDate(r.paid_at),
          r.fulfillment_status ?? "-",
        ])}
      />
      <FunnelModal
        open={open === "abandoned"}
        onClose={() => setOpen(null)}
        title="Abandoned checkouts (unpaid)"
        count={abandonedCount}
        columns={["Email", "Company", "Amount", "Created"]}
        rows={(data.abandoned?.rows ?? []).map((r) => [
          r.email ?? "-",
          r.company,
          r.amount_cents != null
            ? `${(r.currency ?? "usd").toUpperCase()} ${(r.amount_cents / 100).toFixed(2)}`
            : "-",
          fmtDate(r.created_at),
        ])}
      />
      <FunnelModal
        open={open === "ikomatch"}
        onClose={() => setOpen(null)}
        title="ikomatch clicks"
        count={ikoCount}
        columns={["Email", "Placement", "Created"]}
        rows={(data.ikomatch?.rows ?? []).map((r) => [
          r.email ?? "Anonymous",
          r.placement,
          fmtDate(r.created_at),
        ])}
      />
      <FunnelModal
        open={open === "investors"}
        onClose={() => setOpen(null)}
        title="Investors registered"
        count={investorsCount}
        columns={["Name", "Contact email", "Active", "Open to program", "Created"]}
        rows={(data.investors?.rows ?? []).map((r) => [
          r.name,
          r.contact_email ?? "-",
          r.active ? "yes" : "no",
          r.open_to_program ? "yes" : "no",
          fmtDate(r.created_at),
        ])}
      />
      <FunnelModal
        open={open === "referrals"}
        onClose={() => setOpen(null)}
        title="Referrals delivered"
        count={referralsCount}
        columns={["Company / headline", "Program", "Delivered", "Emailed"]}
        rows={(data.referrals?.rows ?? []).map((r) => [
          r.company,
          r.program_name,
          fmtDate(r.delivered_at),
          fmtDate(r.emailed_at),
        ])}
      />
    </div>
  );
}

function fmtDate(iso: string | null) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function FunnelCard({
  label, count, hint, onClick,
}: { label: string; count: number; hint?: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left border border-line rounded-2xl p-4 bg-surface hover:bg-surface-sunken transition"
    >
      <div className="text-[12px] uppercase tracking-wider text-ink-tertiary">{label}</div>
      <div className="mt-2 font-display font-semibold text-[24px]">{count.toLocaleString()}</div>
      {hint && <div className="mt-1 text-[11px] text-ink-tertiary">{hint}</div>}
      <div className="mt-2 text-[11px] text-accent">View list →</div>
    </button>
  );
}

function FunnelModal({
  open, onClose, title, count, columns, rows,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  count: number;
  columns: string[];
  rows: (string | number)[][];
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{title} - {count.toLocaleString()}</DialogTitle>
          <DialogDescription>Underlying records (admin only).</DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] overflow-auto border border-line rounded-lg">
          {rows.length === 0 ? (
            <div className="p-6 text-center text-ink-tertiary text-[13px]">Nothing here yet.</div>
          ) : (
            <table className="w-full text-[12px]">
              <thead className="bg-surface-sunken sticky top-0">
                <tr>
                  {columns.map((c) => (
                    <th key={c} className="text-left px-3 py-2 font-medium text-ink-secondary">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-line">
                    {r.map((cell, j) => (
                      <td key={j} className="px-3 py-2 align-top text-ink">{String(cell)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Business health (super_admin only). Adds metrics the page does not
// already show: net of refunds, SLA timing, affiliate share, country mix.
// ============================================================

function rsMinor(minor: number) {
  return `Rs ${Math.round(minor / 100).toLocaleString("en-IN")}`;
}

function countryLabel(code: string) {
  if (code === "Unknown") return "Unknown";
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

function BusinessHealth({ from, to }: { from: string; to: string }) {
  const run = useServerFn(getBusinessOverview);
  const [data, setData] = useState<BusinessOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    run({ data: { from, to } })
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setErr(e?.message ?? "Could not load business health"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [from, to, run]);

  const f = data?.fulfilment;
  const hours = f?.avgCompleteHours;
  const countries = data?.byCountry ?? [];
  const countryTotal = countries.reduce((s, c) => s + c.count, 0);

  return (
    <div className="mb-6">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="font-display text-[18px] font-semibold">Business health</h2>
        <span className="text-[12px] text-ink-tertiary">
          {loading ? "Loading..." : "super admin only · INR · selected date range"}
        </span>
      </div>
      {err && <div className="mb-3 text-[13px] text-red">{err}</div>}
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${loading && data ? "opacity-60" : ""}`}>
        <div className="bg-surface border border-line rounded-2xl p-5">
          <div className="text-[12px] uppercase tracking-wider text-ink-tertiary">Net revenue</div>
          <div className="mt-2 font-display font-semibold text-[24px]">
            {data ? rsMinor(data.revenue.netMinor) : "-"}
          </div>
          <div className="mt-1 text-[12px] text-ink-tertiary">
            Refunds: {data ? rsMinor(data.revenue.refundsMinor) : "-"}
          </div>
        </div>

        <div className="bg-surface border border-line rounded-2xl p-5">
          <div className="text-[12px] uppercase tracking-wider text-ink-tertiary">Fulfilment SLA</div>
          <div className="mt-2 font-display font-semibold text-[24px]">
            {f && f.slaWithin48Pct != null ? `${f.slaWithin48Pct.toFixed(1)}%` : "-"}
          </div>
          <div className="mt-1 text-[12px] text-ink-tertiary">within 48h</div>
          <div className="mt-3 space-y-1 text-[12px]">
            <div className="flex justify-between gap-2">
              <span className="text-ink-tertiary">Avg completion</span>
              <span className="font-medium tabular-nums">
                {hours == null ? "-" : hours < 1 ? `${Math.round(hours * 60)} min` : `${hours.toFixed(1)} h`}
              </span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-ink-tertiary">Overdue now</span>
              <span className={`font-medium tabular-nums ${f && f.overdueNow > 0 ? "text-red" : ""}`}>
                {f ? f.overdueNow.toLocaleString() : "-"}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-surface border border-line rounded-2xl p-5">
          <div className="text-[12px] uppercase tracking-wider text-ink-tertiary">Affiliate-driven</div>
          <div className="mt-2 font-display font-semibold text-[24px]">
            {data ? data.affiliate.affiliateOrders.toLocaleString() : "-"}
          </div>
          <div className="mt-1 text-[12px] text-ink-tertiary">orders in range</div>
          <div className="mt-3 flex justify-between gap-2 text-[12px]">
            <span className="text-ink-tertiary">Commissions owed</span>
            <span className="font-medium tabular-nums">
              {data ? rsMinor(data.affiliate.commissionsOwedMinor) : "-"}
            </span>
          </div>
        </div>

        <div className="bg-surface border border-line rounded-2xl p-5">
          <div className="text-[12px] uppercase tracking-wider text-ink-tertiary">Orders by country</div>
          {!data ? (
            <div className="mt-2 text-[13px] text-ink-tertiary">-</div>
          ) : countries.length === 0 ? (
            <div className="mt-2 text-[13px] text-ink-tertiary">No paid orders in this range.</div>
          ) : (
            <ul className="mt-2 max-h-[140px] overflow-auto space-y-1.5 text-[13px]">
              {countries.map((c) => (
                <li key={c.country} className="flex justify-between gap-2">
                  <span className="truncate text-ink-secondary">{countryLabel(c.country)}</span>
                  <span className="tabular-nums">
                    {c.count.toLocaleString()}
                    <span className="text-ink-tertiary">
                      {countryTotal ? ` · ${((c.count / countryTotal) * 100).toFixed(1)}%` : ""}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
